import useAuthState from '@/states/auth.state';

export interface UnifiedUserInfo {
  id: string;
  name: string;
  email: string;
  username?: string;
  avatar_url?: string;
  is_admin?: boolean;
}

/**
 * Get unified user information regardless of auth method
 */
export const useUnifiedUserInfo = (): UnifiedUserInfo | null => {
  const { keycloakUserInfo } = useAuthState();

  if (keycloakUserInfo) {
    return {
      id: keycloakUserInfo.sub,
      name: keycloakUserInfo.name || `${keycloakUserInfo.given_name || ''} ${keycloakUserInfo.family_name || ''}`.trim() || keycloakUserInfo.preferred_username || 'Unknown User',
      email: keycloakUserInfo.email,
      username: keycloakUserInfo.preferred_username,
      // Keycloak doesn't provide avatar_url by default, use picture or custom field
      avatar_url: keycloakUserInfo.picture || keycloakUserInfo.avatar_url || undefined,
      is_admin: undefined, // Not available in standard Keycloak user info
    };
  }


  return null;
};

/**
 * Get user email for API calls that require username/email
 */
export const useUserEmail = (): string | null => {
  const userInfo = useUnifiedUserInfo();
  return userInfo?.email ?? null;
};

/**
 * Get user display name
 */
export const useUserDisplayName = (): string | null => {
  const userInfo = useUnifiedUserInfo();
  return userInfo?.name ?? null;
};

/**
 * Check if user is admin (only available for AlurKerja SSO)
 */
export const useIsUserAdmin = (): boolean => {
  const userInfo = useUnifiedUserInfo();
  return userInfo?.is_admin ?? false;
};

/**
 * Get avatar URL (only available for AlurKerja SSO)
 */
export const useUserAvatarUrl = (): string | null => {
  const userInfo = useUnifiedUserInfo();
  return userInfo?.avatar_url ?? null;
};